import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useEffect, useMemo, useState } from 'react';

import SideScroller from '@/components/SideScroller/SideScroller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchSavedSearchesIfNeeded } from '@/redux/modules/savedSearch';
import { createSaveSearchKey, getHasSavedSearches, getSaveSearchState } from '@/redux/modules/saveSearch';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SeeAllLink } from '../SeeAllLink/SeeAllLink';
import items from './options';
import { SearchFollowSuggestionsCard } from './SearchFollowSuggestionsCard';

type Props = {
    className?: string;
    useAltHeader?: boolean;
};

export const SearchFollowSuggestions = memo<Props>(({ className, useAltHeader }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { byParams } = useAppSelector(getSaveSearchState);
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const hasSavedSearches = useAppSelector(getHasSavedSearches);
    const [startedWithSavedSearches] = useState(hasSavedSearches);

    useEffect(() => {
        dispatch(fetchSavedSearchesIfNeeded());
    }, [dispatch, isLoggedIn]);

    const carouselCards = useMemo(
        () =>
            items?.map((item, index) => {
                const saveSearchParams = createSaveSearchKey(item.categoryId);
                const relevantSaveSearch = byParams[saveSearchParams];
                return {
                    card: (
                        <SearchFollowSuggestionsCard
                            categoryId={item.categoryId}
                            categoryUrl={item.categoryUrl}
                            followerCount={item.followerCount}
                            images={item.images}
                            itemCount={item.itemCount}
                            name={item.name}
                            saveSearchStateKey={relevantSaveSearch?.key ?? ''}
                        />
                    ),
                    key: `${index}-${item.categoryId}`,
                };
            }),
        [byParams]
    );

    if (startedWithSavedSearches) {
        return null;
    }

    return (
        <div
            className={cn('flex flex-col', className)}
            id="followedSearch"
        >
            {useAltHeader ? (
                <div className="mx-[20px] mb-[20px] mt-0 w-full max-w-[1200px] self-center font-secondary text-[18px] text-text-primary">
                    {formatMessage(['searchFollowSuggestions', 'altHeader'])}
                </div>
            ) : (
                <>
                    <div className="mb-2xs flex">
                        <div className="flex flex-auto flex-row items-baseline justify-start text-text-primary xsMax:ml-[20px] [&_h2]:m-0 [&_h2]:mr-[15px] [&_h2]:inline-block [&_h2]:p-0">
                            <H2>{formatMessage(['searchFollowSuggestions', 'header'])}</H2>
                            <SeeAllLink
                                text={String(formatMessage(['searchFollowSuggestions', 'manage']))}
                                url="/dashboard/followed-searches"
                            />
                        </div>
                    </div>
                    <BodySecondary
                        className="mb-[20px] tracking-[0.3px] xsMax:ml-[20px]"
                        color="secondary"
                    >
                        {formatMessage(['searchFollowSuggestions', 'subTitle'])}
                    </BodySecondary>
                </>
            )}
            <SideScroller
                cards={carouselCards}
                className="max-w-[1200px]"
                useOverlayControls
            />
        </div>
    );
});
